import React, { useMemo, useState } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Card,
  CardHeader,
  InputAdornment,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import SearchIcon from "@mui/icons-material/Search";
import StatusMark from "./StatusMark";
import { styles } from "../styles/publicStyles";
import FactCheckOutlinedIcon from "@mui/icons-material/FactCheckOutlined";
import SectionNav from "./SectionNav";

// DOM id of a control area's block, for the left navigation.
export const domainElementId = (domain) =>
  `controls-${String(domain)
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")}`;

// Same pill look as People "In Scope / Out of Scope".
const pillGroupSx = {
  "& .MuiToggleButton-root": {
    textTransform: "none",
    px: 1.5,
    py: 0.25,
    fontSize: 13,
    border: "1px solid #E8EAED",
    "&.Mui-selected": {
      backgroundColor: "#3B82F6",
      color: "#FFFFFF",
      "&:hover": { backgroundColor: "#3B82F6" },
    },
  },
};

const ControlsSection = ({ controls }) => {
  const theme = useTheme();
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("all");
  // Every area starts open; visitors close the ones they are done with.
  const [closed, setClosed] = useState(() => new Set());

  const domains = useMemo(() => {
    const term = search.trim().toLowerCase();
    const groups = new Map();
    controls.forEach((control) => {
      if (!groups.has(control.domain)) groups.set(control.domain, []);
      groups.get(control.domain).push(control);
    });
    return [...groups.entries()]
      .map(([domain, all]) => ({
        domain,
        total: all.length,
        passing: all.filter((c) => c.status === "pass").length,
        failing: all.filter((c) => c.status === "fail").length,
        visible: all.filter(
          (c) =>
            (status === "all" || c.status === status) &&
            (!term ||
              c.title.toLowerCase().includes(term) ||
              c.name.toLowerCase().includes(term) ||
              (c.description || "").toLowerCase().includes(term) ||
              domain.toLowerCase().includes(term))
        ),
      }))
      .filter((group) => group.visible.length > 0);
  }, [controls, search, status]);

  // The company chooses what to show: all controls, only passing ones, or
  // controls with no status. The Passing / Failing filter and "x/y passing"
  // only make sense when failing controls are shown.
  const showsFailing = controls.some((c) => c.status === "fail");
  const showsStatus = controls.some((c) => c.status === "pass");

  return (
    <Stack
      direction={{ xs: "column", md: "row" }}
      spacing={{ xs: 2, md: 4 }}
      alignItems="flex-start"
    >
      <SectionNav
        title="Control areas"
        icon={<FactCheckOutlinedIcon />}
        label="Control areas"
        items={domains.map((group) => ({
          id: group.domain,
          name: group.domain,
          count: group.visible.length,
        }))}
        elementId={(item) => domainElementId(item.id)}
      />
      <Card
        id="controls"
        sx={{ ...styles.mainCard, flex: 1, minWidth: 0, width: "100%" }}
      >
        <CardHeader
          sx={styles.cardHeader}
          title={
            <Stack
              useFlexGap
              direction="row"
              alignItems="center"
              spacing={2}
              sx={{ flexWrap: "wrap", rowGap: 1 }}
            >
              <span>Controls</span>
              {showsFailing && (
                <ToggleButtonGroup
                  size="small"
                  exclusive
                  value={status}
                  onChange={(e, value) => value && setStatus(value)}
                  sx={pillGroupSx}
                >
                  <ToggleButton value="all">All</ToggleButton>
                  <ToggleButton value="pass">Passing</ToggleButton>
                  <ToggleButton value="fail">Failing</ToggleButton>
                </ToggleButtonGroup>
              )}
            </Stack>
          }
          subheader={
            showsStatus
              ? "Checked automatically, every day, against our live systems."
              : "The security controls we have in place."
          }
          action={
            <TextField
              size="small"
              placeholder="Search controls"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              sx={{
                width: { xs: "100%", sm: 240 },
                backgroundColor: "#FFFFFF",
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />
          }
        />
        <Box sx={{ p: 2 }}>
          {domains.length === 0 && (
            <Typography
              variant="body2"
              sx={{ color: theme.customColors.text.secondary, p: 2 }}
            >
              No controls match your search.
            </Typography>
          )}
          {domains.map((group) => (
            <Accordion
              key={group.domain}
              id={domainElementId(group.domain)}
              disableGutters
              elevation={0}
              expanded={!closed.has(group.domain)}
              onChange={(_, open) =>
                setClosed((prev) => {
                  const next = new Set(prev);
                  if (open) next.delete(group.domain);
                  else next.add(group.domain);
                  return next;
                })
              }
              sx={{
                border: `1px solid ${theme.customColors.border.main}`,
                borderRadius: "8px !important",
                overflow: "hidden",
                mb: 1,
                scrollMarginTop: "96px",
                "&:before": { display: "none" },
              }}
            >
              {/* The area reads as a heading (the app's header shade and
                  subtitle style), above the control titles in its list. */}
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                sx={{
                  backgroundColor: theme.customColors.background.header,
                  borderBottom: `1px solid ${theme.customColors.border.main}`,
                }}
              >
                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                  sx={{ width: "100%", pr: 2 }}
                >
                  <Typography variant="subtitle1" fontWeight="500">
                    {group.domain}
                  </Typography>
                  <Box sx={styles.countBox}>
                    {/* One format for every group, decided by what the
                      whole list shows, so "3/3" never sits next to "3". */}
                    {showsFailing
                      ? `${group.passing}/${group.passing + group.failing} passing`
                      : showsStatus
                        ? `${group.passing} passing`
                        : `${group.total} ${group.total === 1 ? "control" : "controls"}`}
                  </Box>
                </Stack>
              </AccordionSummary>
              <AccordionDetails sx={{ pt: 0 }}>
                {group.visible.map((control) => (
                  <Stack
                    key={control.id}
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                    spacing={2}
                    sx={{
                      py: 1.25,
                      borderTop: `1px solid ${theme.customColors.border.main}`,
                      "&:first-of-type": { borderTop: "none" },
                    }}
                  >
                    {/* The title in the app's strong text, so it stands
                        apart from the description under it. */}
                    <Box sx={{ minWidth: 0 }}>
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: 500,
                          color: theme.customColors.text.strong,
                        }}
                      >
                        {control.title}
                      </Typography>
                      {/* What the control means, not its internal code. */}
                      {control.description && (
                        <Typography
                          variant="body2"
                          sx={{
                            mt: 0.25,
                            color: theme.customColors.text.secondary,
                            lineHeight: 1.6,
                          }}
                        >
                          {control.description}
                        </Typography>
                      )}
                    </Box>
                    {(control.status === "pass" ||
                      control.status === "fail") && (
                      <StatusMark status={control.status} />
                    )}
                  </Stack>
                ))}
              </AccordionDetails>
            </Accordion>
          ))}
        </Box>
      </Card>
    </Stack>
  );
};

export default ControlsSection;
